import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Pagination } from "@/components/Pagination";
import { MessageCircle, Phone, Mail, Trash2, Search, Globe, Save } from "lucide-react";

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "rejected", label: "Rejected" },
  { value: "success", label: "Success" },
];
const STATUS_STYLES = {
  new: "bg-[#FBF6EC] text-[#9A7B33] border-[#E7DBB8]",
  contacted: "bg-[#EEF5EE] text-[#4B7A4E] border-[#CADFCB]",
  rejected: "bg-[#FBEDED] text-[#B0413E] border-[#EBC7C6]",
  success: "bg-[#EAF1F7] text-[#3A6EA5] border-[#C6DAEC]",
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function WebsiteLeadsPage() {
  const confirm = useConfirm();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(true);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [status, debounced, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE, search: debounced };
      if (status !== "all") params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/website-leads", { params });
      const items = data.items || [];
      setLeads(items); setTotal(data.total || 0); setTotalPages(data.total_pages || 1);
      setNoteDrafts(Object.fromEntries(items.map((l) => [l.id, l.note || ""])));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, status, debounced, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const waLink = (lead) => {
    const digits = (lead.phone || "").replace(/\D/g, "");
    const text = `Hi ${lead.name}, thank you for reaching out to Dejavu Makeup by Vinni! I'd love to help with your ${lead.service || "makeup"} requirement. When is your event date?`;
    return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
  };

  const openWhatsApp = async (lead) => {
    window.open(waLink(lead), "_blank");
    if (lead.status === "new") {
      try { await api.put(`/website-leads/${lead.id}`, { status: "contacted" }); setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: "contacted" } : l))); window.dispatchEvent(new Event("admin-data-updated")); }
      catch { /* non-blocking */ }
    }
  };

  const changeStatus = async (lead, value) => {
    const prevStatus = lead.status;
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: value } : l)));
    try { await api.put(`/website-leads/${lead.id}`, { status: value }); window.dispatchEvent(new Event("admin-data-updated")); }
    catch (err) { toast.error(formatApiError(err)); setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: prevStatus } : l))); }
  };

  const saveNote = async (lead) => {
    const note = noteDrafts[lead.id] ?? "";
    try {
      await api.put(`/website-leads/${lead.id}`, { note });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, note } : l)));
      toast.success("Note saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (lead) => {
    const ok = await confirm({ title: "Delete lead?", description: `${lead.name}'s enquiry will be permanently removed.`, confirmText: "Delete", destructive: true });
    if (!ok) return;
    try { await api.delete(`/website-leads/${lead.id}`); toast.success("Lead deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="website-leads-page">
      <div className="flex items-center gap-3 mb-1">
        <Globe size={22} className="text-[#C88270]" />
        <h1 className="font-serif text-3xl text-[#2A2421]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Website Leads</h1>
      </div>
      <p className="text-xs text-[#5C5652] mb-6">Enquiries synced from dejavumakeup.com — reach out instantly on WhatsApp.</p>

      <div className="flex flex-col gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search" placeholder="Search name, phone, message…"
              className="w-full pl-10 pr-3 py-2.5 bg-white border border-[#EAE7E0] text-sm focus:border-[#C88270] focus:outline-none" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {[["all", "All"], ...STATUS_OPTIONS.map((s) => [s.value, s.label])].map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)} data-testid={`lead-filter-${k}`}
                className={`px-3.5 py-2.5 text-xs uppercase tracking-[0.12em] border transition-colors ${status === k ? "bg-[#2A2421] text-white border-[#2A2421]" : "bg-white border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">Received</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="lead-date-from"
            className="px-3 py-2 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none" />
          <span className="text-[#B8B2AA] text-xs">—</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="lead-date-to"
            className="px-3 py-2 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none" />
          {(dateFrom || dateTo) && <button onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="lead-date-clear" className="text-[10px] uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]">Clear</button>}
          {total > 0 && <span className="text-[10px] text-[#B8B2AA] ml-auto">{total} lead{total > 1 ? "s" : ""}</span>}
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-[#5C5652] text-xs uppercase tracking-[0.3em]">Loading…</div>
      ) : total === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]" data-testid="no-leads">
          No website leads yet. New enquiries from dejavumakeup.com will appear here.
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map((lead) => (
            <div key={lead.id} data-testid={`lead-card-${lead.id}`}
              className="bg-white border border-[#EAE7E0] px-4 py-3 hover:border-[#C88270]/50 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[15px] text-[#2A2421] truncate">{lead.name}</span>
                    <span className={`text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 border rounded-full ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`} data-testid={`lead-status-badge-${lead.id}`}>{(STATUS_OPTIONS.find((s) => s.value === lead.status) || STATUS_OPTIONS[0]).label}</span>
                    {lead.service && <span className="text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-full bg-[#FAF3F0] text-[#C88270] border-[#E4C9C0]">{lead.service}</span>}
                    <span className="text-[10px] text-[#B8B2AA]">{fmtDate(lead.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap text-xs text-[#5C5652]">
                    <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-[#C88270]"><Phone size={11} /> {lead.phone}</a>
                    {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-[#C88270] truncate max-w-[180px]"><Mail size={11} /> {lead.email}</a>}
                  </div>
                  {lead.message && <p className="text-xs text-[#2A2421] mt-1 line-clamp-2 leading-snug" title={lead.message}>{lead.message}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => openWhatsApp(lead)} data-testid={`lead-whatsapp-${lead.id}`} title="Message on WhatsApp"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] uppercase tracking-[0.1em] bg-[#25D366] text-white hover:bg-[#1da851] transition-colors">
                    <MessageCircle size={14} /> <span className="hidden sm:inline">WhatsApp</span>
                  </button>
                  <select value={lead.status} onChange={(e) => changeStatus(lead, e.target.value)} data-testid={`lead-status-${lead.id}`}
                    className="px-2 py-1.5 text-xs bg-white border border-[#EAE7E0] text-[#2A2421] focus:border-[#C88270] focus:outline-none cursor-pointer">
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <button onClick={() => del(lead)} data-testid={`lead-del-${lead.id}`} className="p-1.5 text-[#5C5652] hover:text-red-600 border border-[#EAE7E0] hover:border-red-300"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input value={noteDrafts[lead.id] ?? ""} onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [lead.id]: e.target.value }))} data-testid={`lead-note-${lead.id}`}
                  placeholder="Add a note after your conversation…" className="flex-1 min-w-0 px-2.5 py-1.5 bg-[#FAF9F6] border border-[#EAE7E0] text-xs text-[#2A2421] focus:border-[#C88270] focus:outline-none" />
                <button onClick={() => saveNote(lead)} data-testid={`lead-note-save-${lead.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.1em] border border-[#EAE7E0] text-[#5C5652] hover:border-[#C88270] hover:text-[#C88270]"><Save size={12} /> Save</button>
              </div>
            </div>
          ))}
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} testId="leads-pagination" />
        </div>
      )}
    </div>
  );
}
